import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import ShareIcon from "../../assets/icons/share.svg";
import CopyIcon from "../../assets/icons/copy.svg";
import MapAddressDetails from "../Maps/MapAddressDetails";
import Footer from "../Footer/Footer";
import Header from "../Header/Header";
import { getUserProfile, UserProfile } from "../../API/UserProfileApi/GetUserProfile";
import { Address } from "../../API/Address/ViewAllAddedAddressByUserAsync";
import OfficeIcon from '../../assets/icons/OfficeList.svg';
import HomeIcon from '../../assets/icons/HomeLIst.svg';
import OtherIcon from '../../assets/icons/OtherList.svg';
import FavouriteTrue from "../../assets/icons/favouritetrue.svg";
import FavouriteFalse from "../../assets/icons/favouritefalse.svg";
import LocationIcon from '../../assets/icons/Newmap.svg';
import { useLocalization } from "../Context/MultiLanguageContext";

import {
    EmailShareButton,
    WhatsappShareButton,
    FacebookShareButton,
    TwitterShareButton,
} from "react-share";
import { EmailIcon, WhatsappIcon, FacebookIcon, TwitterIcon } from "react-share";
import CheckIcon from "../../assets/icons/checked.svg";
import { updateLocalStorageSearchHistory } from "../../utils/updateLocalStorageSearchHistory";
import { RemoveFromFavouritesAsync } from "../../API/AddressFavourites/RemoveFromFavouritesAsync";
import { AddtoFavouritesAsync } from "../../API/AddressFavourites/AddToFavouritesAsync";
import { getUserIdFromToken } from "../../auth/authUtils";
import { ShareLink } from "../../API/Address/SharePost";
import { Eye, Image as ImageIcon, X } from "lucide-react";
import { getAddressImageAsync } from "../../API/Address/GetAddressImageAsync";

interface SearchHistoryItem {
    query: string;
    results: any;
    outsideBlock?: boolean;
}

const SearchAddressDetails: React.FC<{ updateUserName: (newUserName: string) => void }> = ({ updateUserName }) => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useLocalization();

    const location = useLocation();
    const stateAddress = (location.state as { address?: Address })?.address;
    const [addressResults, setAddressResults] = useState<Address[]>([]);
    const [recentSearchHistory, setRecentSearchHistory] = useState<SearchHistoryItem[]>([]);
    const [addresses, setAddresses] = useState<any[]>([]);

    const [address, setAddress] = useState<Address | null>(stateAddress || null);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [copiedPamCode, setCopiedPamCode] = useState<string | null>(null);
    const [showShareOptions, setShowShareOptions] = useState(false);
    const [userName, setUserName] = useState('');
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [copiedField, setCopiedField] = useState<{ type: "pamCode" | "digiPin"; value: string } | null>(null);
    const [shareUrls, setShareUrls] = useState<{ [key: string]: { url: string; referralLink?: string } }>({});
    const [isLoadingShare, setIsLoadingShare] = useState<{ [key: string]: boolean }>({});
    const [selectedImage, setSelectedImage] = useState<{ id: number; url: string } | null>(null);
    const [displayImages, setDisplayImages] = useState<{ id: number; url: string }[]>([]);
    const [isLoadingImages, setIsLoadingImages] = useState(false);
    const imageMetadata = (address as any)?.images;

    useEffect(() => {
        const storedUserName = localStorage.getItem('userName');
        if (storedUserName) {
            setUserName(storedUserName);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        const objectUrls: string[] = [];
        const images = imageMetadata;

        if (!Array.isArray(images) || images.length === 0) {
            setDisplayImages([]);
            setIsLoadingImages(false);
            return;
        }

        const loadImages = async () => {
            setIsLoadingImages(true);
            const token = localStorage.getItem("token") || "";
            const loadedImages = await Promise.all(
                images.map(async (image: any, index: number) => {
                    const imageId = Number(image?.id);
                    if (!Number.isFinite(imageId)) {
                        console.error("Address image is missing a valid image ID.", image);
                        return null;
                    }

                    try {
                        const imageBlob = await getAddressImageAsync(
                            Number(address?.id),
                            imageId,
                            token,
                            t
                        );

                        if (!imageBlob || cancelled) return null;

                        const url = URL.createObjectURL(imageBlob);
                        objectUrls.push(url);
                        return { id: imageId || index, url };
                    } catch (error) {
                        console.error(`Failed to load address image ${imageId}:`, error);
                        return null;
                    }
                })
            );

            if (!cancelled) {
                setDisplayImages(loadedImages.filter(
                    (image): image is { id: number; url: string } => image !== null
                ));
                setIsLoadingImages(false);
            }
        };

        void loadImages();

        return () => {
            cancelled = true;
            objectUrls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, [address?.id, imageMetadata, t]);

    const revertFavouriteState = (addressId: number, originalState: boolean) => {
        setAddress(prev =>
            prev && Number(prev.id) === addressId ? { ...prev, isFavourite: originalState } : prev
        );
    };

    const HeartClick = async (addressId: number, isFavourite: boolean) => {
        setAddress(prev =>
            prev && Number(prev.id) === addressId ? { ...prev, isFavourite: !prev.isFavourite } : prev
        );

        const token = localStorage.getItem('token');
        if (!token) {
            return;
        }

        try {
            if (!isFavourite) {
                await AddtoFavouritesAsync(addressId, token);
                updateLocalStorageSearchHistory(addressId, true);
            } else {
                await RemoveFromFavouritesAsync(addressId, token);
                updateLocalStorageSearchHistory(addressId, false);
            }
            const userId = getUserIdFromToken();
            const storedHistory = localStorage.getItem(`recentSearch_${userId}`);
            if (storedHistory) {
                setRecentSearchHistory(JSON.parse(storedHistory));
            }
        } catch (error) {
            revertFavouriteState(addressId, isFavourite);
        }
    };

    const fetchAndHandleUserProfile = async () => {
        try {
            const data = await getUserProfile(t);
            setUserProfile(data);
            setUserName(data.userName);
            updateUserName(data.userName);
            navigate('/profile', {
                state: {
                    userName: data.userName,
                    email: data.email,
                    phoneNumber: data.phoneNumber,
                },
            });
        } catch (error) {
            toast.error(t("sessionExpired"));
            navigate('/home');
        }
    };

    useEffect(() => {
        if (!address) {
            setErrorMessage(t("addressdataNotAvailable"));
        }
    }, [address]);

    const formatPamCode = (address: Address): string => {
        const pin = String(address?.pinCode ?? "").trim();
        const block = String(address?.blockCode ?? "").trim();
        const addr = String(address?.addressCode ?? "").trim();

        if (!pin && !block && !addr) return "N/A";

        const parts = [pin, block, addr].filter(part => part);
        return parts.join("-");
    };

    const formatPamCodeForCopy = (address: Address): string => {
        const pin = String(address?.pinCode ?? "").trim();
        const block = String(address?.blockCode ?? "").trim();
        const addr = String(address?.addressCode ?? "").trim();

        if (!pin && !block && !addr) return "N/A";

        const parts = [pin, block, addr].filter(part => part);
        return parts.join("-");
    };

    const handleLocationIconClick = (latitude: number, longitude: number) => {
        const defaultMap = localStorage.getItem("defaultMap") || "google";

        if (defaultMap === "waze") {
            const wazeMapsUrl = ``;
            window.open(wazeMapsUrl, '_blank');
        } else if (defaultMap === "mapindia") {
            const mapplsUrl = `https://www.mappls.com/@${latitude},${longitude}`;
            window.open(mapplsUrl, "_blank");
        } else if (defaultMap === "apple") {
            const appleMapsUrl = `https://maps.apple.com/?ll=${latitude},${longitude}&q=${latitude},${longitude}&z=18&t=m`;
            window.open(appleMapsUrl, "_blank");
        } else {
            const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
            window.open(googleMapsUrl, '_blank');
        }
    };

    const handleShareClick = async () => {
        if (showShareOptions) {
            setShowShareOptions(false);
            return;
        }

        if (!address?.id) {
            console.error("No address ID available for sharing");
            return;
        }

        setIsLoadingShare(prev => ({ ...prev, [address.id]: true }));

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                toast.error(t("sessionExpired"));
                navigate('/');
                return;
            }

            const shareResponse = await ShareLink(parseInt(address.id), token);

            if (shareResponse && shareResponse.url) {
                setShareUrls(prev => ({
                    ...prev,
                    [address.id]: {
                        url: shareResponse.url,
                        referralLink: shareResponse.referralLink
                    }
                }));
                setShowShareOptions(true);
            } else {
                toast.error(t("errorGeneratingShareLink"));
            }
        } catch (error) {
            console.error('Error calling share API:', error);
            toast.error(t("errorGeneratingShareLink"));
        } finally {
            setIsLoadingShare(prev => ({ ...prev, [address.id]: false }));
        }
    };

    const handleCopy = async (value: string, type: "pamCode" | "digiPin") => {
        if (!value) return;
        try {
            const finalValue = String(value ?? "").trim();
            await navigator.clipboard.writeText(finalValue);
            setCopiedField({ type, value: finalValue });
            setTimeout(() => setCopiedField(null), 2000);
        } catch (err) {
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('userToken');
        localStorage.removeItem('authToken');
        localStorage.removeItem('token');
        localStorage.removeItem('userName');

        sessionStorage.clear();
        navigate("/", { replace: true });
    };

    const shareMessage = (address: Address) => {
        const shareData = shareUrls[address.id];
        const dynamicUrl = shareData?.url;
        const referralLink = shareData?.referralLink;
        const fallbackUrl = `https://app.getmethere.in/${formatPamCodeForCopy(address)}`;

        const addressLink = dynamicUrl || fallbackUrl;

        const isPreprod = process.env.REACT_APP_API_BASE_URL === "https://preprod-api.getmethere.in";
        const firstName = (userName || "").trim().split(/\s+/)[0] || "";
        const userId = getUserIdFromToken();
        const userRefLink = referralLink
            ? referralLink
            : (isPreprod
                ? `https://preprod.getmethere.in/?ref=${userId || ""}&by=${firstName}`
                : `https://getmethere.in/?ref=${userId || ""}&by=${firstName}`);

        return `Check out this address:
${address.addressLine1}, ${address.addressLine2}, ${address.addressLine3}
PAM Code: ${formatPamCode(address)}
Digi Pin: ${address?.digiPin || "N/A"}
Address Link: ${addressLink}

More details: ${userRefLink}`;
    };

    return (
        <div className="min-h-screen bg-gray-100">
            <ToastContainer />
            <Header userName={userName} fetchUserProfile={fetchAndHandleUserProfile} handleLogout={handleLogout} />
            <main className="container mx-auto px-4 py-6">
                <div className="bg-white rounded-md p-4 md:p-6 ">
                    <div className="relative h-[250px] sm:h-[300px] md:h-[350px]">
                        {address ? (
                            <MapAddressDetails addresses={[address]} />
                        ) : (
                            <p className="text-center text-gray-500">{t("loadingMap")}</p>
                        )}
                    </div>
                    {successMessage && (
                        <p className="mt-4 text-center text-green-500">{successMessage}</p>
                    )}
                    {address ? (
                        <div className="mt-2">
                            <div className="p-4 bg-white rounded-lg relative">
                                <div className="flex flex-wrap items-center gap-2 text-base md:text-lg font-bold mb-2">
                                    {address.addressType.toLowerCase() === 'office' && (
                                        <>
                                            <img src={OfficeIcon} alt="Office Icon" className="w-10 h-10" />
                                            <span>{t("office")}</span>
                                        </>
                                    )}
                                    {address.addressType.toLowerCase() === 'home' && (
                                        <>
                                            <img src={HomeIcon} alt="Home Icon" className="w-10 h-10" />
                                            <span>{t("home")}</span>
                                        </>
                                    )}
                                    {address.addressType.toLowerCase() === 'other' && (
                                        <>
                                            <img src={OtherIcon} alt="Other Icon" className="w-8 h-8 sm:w-10 sm:h-10" />
                                            <span>Other</span>
                                        </>
                                    )}
                                    {!['office', 'home', 'other'].includes(address.addressType.toLowerCase()) && (
                                        <>
                                            <img src={OtherIcon} alt="Custom Type Icon" className="w-8 h-8 sm:w-10 sm:h-10" />
                                            <span>{address.addressType}</span>
                                        </>
                                    )}
                                </div>

                                <div className="space-y-1">
                                    <p>{t("buildingNumber")} : {address.addressLine1}</p>
                                    <p>{t("street")} : {address.addressLine2}</p>
                                    <p>{t("city")} : {address.addressLine3}</p>
                                    {/* <p>{t("postalCode")} : {address.pinCode}</p> */}
                                    <p>{t("postOffice")} : {address.postOfficeName}</p>
                                </div>

                                <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mt-4">
                                    <div className="flex flex-col md:flex-row md:items-center gap-4 w-full xl:w-auto">
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
                                            <label htmlFor="pamCode" className="text-sm md:text-base font-medium whitespace-nowrap w-24">
                                                {t("pamCode")} :
                                            </label>
                                            <div className="relative flex-1 sm:w-auto md:w-[180px] lg:w-[200px]">
                                                <input
                                                    id="pamCode"
                                                    type="text"
                                                    value={formatPamCode(address)}
                                                    className="text-sm md:text-base lg:text-lg p-2 pr-10 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    readOnly
                                                />
                                                <button
                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500 transition-colors"
                                                    aria-label="Copy PAM Code"
                                                    onClick={() => handleCopy(formatPamCode(address), "pamCode")}
                                                >
                                                    <img
                                                        src={copiedField?.type === "pamCode" && copiedField.value === formatPamCode(address) ? CheckIcon : CopyIcon}
                                                        alt="Copy"
                                                        className="w-5 h-5 md:w-6 md:h-6"
                                                    />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
                                            <label htmlFor="digipin" className="text-sm md:text-base font-medium whitespace-nowrap w-24">
                                                {t("digiPin")} :
                                            </label>
                                            <div className="relative flex-1 sm:w-auto md:w-[180px] lg:w-[200px]">
                                                <input
                                                    id="digipin"
                                                    type="text"
                                                    value={address.digiPin}
                                                    className="text-sm md:text-base p-2 pr-10 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    readOnly
                                                />
                                                <button
                                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-blue-500 transition-colors"
                                                    aria-label="Copy DigiPin"
                                                    onClick={() => handleCopy(address?.digiPin || '', "digiPin")}
                                                >
                                                    <img
                                                        src={copiedField?.type === "digiPin" && copiedField.value === address?.digiPin ? CheckIcon : CopyIcon}
                                                        alt="Copy"
                                                        className="w-5 h-5 md:w-6 md:h-6"
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2 justify-center sm:justify-start xl:justify-end items-center mt-2 xl:mt-0">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                HeartClick(Number(address.id), address.isFavourite);
                                            }}
                                            title="Toggle Favourites"
                                            className="w-12 h-10 sm:w-16 sm:h-12 md:w-20 md:h-12 lg:w-24 lg:h-12 flex items-center justify-center p-1 border border-gray-200 bg-white rounded-md hover:bg-blue-100 focus:outline-none transition-colors"
                                        >
                                            <img
                                                src={address.isFavourite ? FavouriteTrue : FavouriteFalse}
                                                alt="Favourites"
                                                className="w-5 h-5 sm:w-6 sm:h-6"
                                            />
                                        </button>
                                        <button
                                            onClick={handleShareClick}
                                            disabled={isLoadingShare[address?.id]}
                                            title="Share Address"
                                            className={`w-12 h-10 sm:w-16 sm:h-12 md:w-20 md:h-12 lg:w-24 lg:h-12 flex items-center justify-center p-1 border border-gray-200 bg-white rounded-md hover:bg-blue-100 focus:outline-none transition-colors ${isLoadingShare[address?.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {isLoadingShare[address?.id] ? (
                                                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                            ) : (
                                                <img
                                                    src={ShareIcon}
                                                    alt="Share"
                                                    className="w-5 h-5 sm:w-6 sm:h-6"
                                                />
                                            )}
                                        </button>
                                        <button
                                            title="Open in Maps"
                                            className="w-12 h-10 sm:w-16 sm:h-12 md:w-20 md:h-12 lg:w-24 lg:h-12 flex items-center justify-center p-1 border border-gray-200 bg-white rounded-md hover:bg-blue-100 focus:outline-none transition-colors"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleLocationIconClick(address.latitude, address.longitude);
                                            }}
                                        >
                                            <img
                                                src={LocationIcon}
                                                alt="Location"
                                                className="w-5 h-5 sm:w-6 sm:h-6"
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {((address as any)?.url || (address as any)?.notes || (address as any)?.images?.length > 0 || isLoadingImages) && (
                                <section className="mt-6 min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-5" aria-labelledby="address-meta-heading">
                                    <div className="mb-4 flex items-center gap-2">
                                        <ImageIcon className="h-5 w-5 text-slate-600" aria-hidden="true" />
                                        <h3 id="address-meta-heading" className="text-base font-semibold text-slate-900">
                                            Address details
                                        </h3>
                                    </div>

                                    <div className="space-y-4">
                                        {(address as any)?.url && (
                                            <div>
                                                <p className="mb-1 text-sm font-medium text-slate-700">URL</p>
                                                <a
                                                    href={(address as any).url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="break-all text-xs text-blue-600 underline [overflow-wrap:anywhere] sm:text-sm"
                                                >
                                                    {(address as any).url}
                                                </a>
                                            </div>
                                        )}

                                        {(address as any)?.notes && (
                                            <div>
                                                <p className="mb-1 text-sm font-medium text-slate-700">Notes</p>
                                                <p className="whitespace-pre-wrap break-words text-sm text-slate-600">{(address as any).notes}</p>
                                            </div>
                                        )}

                                        {((address as any)?.images?.length > 0 || isLoadingImages) && (
                                            <div>
                                                <p className="mb-2 text-sm font-medium text-slate-700">Images</p>
                                                {isLoadingImages ? (
                                                    <p className="text-sm text-slate-500">Loading images...</p>
                                                ) : (
                                                    <div className="flex w-full min-w-0 snap-x snap-mandatory flex-nowrap gap-3 overflow-x-auto overscroll-x-contain pb-2">
                                                        {displayImages.map((image) => (
                                                            <div
                                                                key={image.id}
                                                                className="group relative aspect-[4/3] w-[min(78vw,260px)] flex-none snap-start overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"
                                                            >
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setSelectedImage(image)}
                                                                    className="block h-full w-full bg-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                                                                    aria-label="View address image"
                                                                >
                                                                    <img
                                                                        src={image.url}
                                                                        alt="Address image"
                                                                        className="h-full w-full object-cover"
                                                                    />
                                                                    <span className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md bg-black/60 text-white group-hover:bg-black/80">
                                                                        <Eye className="h-4 w-4" aria-hidden="true" />
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </section>
                            )}

                            {showShareOptions && (
                                <div className="flex gap-4 justify-end mr-20">
                                    <EmailShareButton
                                        url={shareMessage(address)}
                                        className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                                    >
                                        <EmailIcon size={32} round style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', borderRadius: '50%' }} />
                                    </EmailShareButton>
                                    <WhatsappShareButton
                                        url={shareMessage(address)}
                                        className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                                    >
                                        <WhatsappIcon size={32} round style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', borderRadius: '50%' }} />
                                    </WhatsappShareButton>
                                    <FacebookShareButton
                                        url={shareMessage(address)}
                                        className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                                    >
                                        <FacebookIcon size={32} round style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', borderRadius: '50%' }} />
                                    </FacebookShareButton>
                                    <TwitterShareButton
                                        url={shareMessage(address)}
                                        className="transform transition-transform duration-300 hover:scale-150 hover:shadow-lg"
                                    >
                                        <TwitterIcon size={32} round style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)', borderRadius: '50%' }} />
                                    </TwitterShareButton>
                                </div>
                            )}

                        </div>
                    ) : (
                        <p className="mt-6 text-center text-gray-500">{t("loadingAddressDetails")}</p>
                    )}
                </div>
            </main>

            {selectedImage && (
                <div
                    className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/60 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Maximized address image"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="relative flex max-h-[90vh] max-w-[calc(100vw-2rem)] items-center justify-center" onClick={(event) => event.stopPropagation()}>
                        <img src={selectedImage.url} alt="Maximized address image" className="max-h-[90vh] max-w-full rounded-lg object-contain shadow-2xl" />
                        <button
                            type="button"
                            onClick={() => setSelectedImage(null)}
                            className="absolute right-2 top-2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
                            aria-label="Close maximized image"
                        >
                            <X className="h-5 w-5" aria-hidden="true" />
                        </button>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default SearchAddressDetails;